import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { getHuckConfig } from '../utils/getHuckConfig.js';
import { triggerSurveyEvent } from './useHotjar.js';

/**
 * Intercepts clicks on the header podlet and triggers a Hotjar survey event.
 * Prevents navigation to track when users attempt to exit the wizard via header clicks.
 * Only active when Hotjar on exit toggle is enabled. Removes listener after first header click.
 */
export const useHeaderClickInterceptor = () => {
  const location = useLocation();

  useEffect(() => {
    // SSR safety check
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const config = getHuckConfig();

    // Only apply interceptor if feature toggle is enabled
    if (!config.toggles.mobilityItemInsertionHotjarOnExit) {
      return;
    }

    // Only apply interceptor if Hotjar is enabled
    if (!config.canUseHotjar) {
      return;
    }

    // Only apply interceptor on the final step (step 5)
    if (!location.pathname.endsWith('/steps/5')) {
      return;
    }

    let isListenerActive = true;

    /**
     * Attempts to find the header element using multiple selectors.
     * Tries brand-specific selectors first, then falls back to generic ones.
     */
    const findHeaderElement = (target: HTMLElement): HTMLElement | null => {
      // Try multiple selectors in order of specificity
      const selectors = [
        'header',
        '[class*="topbar"]',
        '[class*="header"]',
        'finn-topbar',
        'nav[role="banner"]',
        'nav',
        '[data-podlet-name*="header"]',
      ];

      for (const selector of selectors) {
        const element = target.closest(selector);
        if (element) {
          return element as HTMLElement;
        }
      }

      return null;
    };

    const handleHeaderClick = (event: MouseEvent) => {
      if (!isListenerActive) {
        return;
      }

      const target = event.target as HTMLElement;
      const headerElement = findHeaderElement(target);

      if (headerElement) {
        // Block navigation
        event.preventDefault();
        event.stopPropagation();

        // Trigger Hotjar survey event
        triggerSurveyEvent('header-logo-click');

        // Remove listener after first header click
        isListenerActive = false;
        document.removeEventListener('click', handleHeaderClick, true);
      }
    };

    // Wait for header to be available before setting up listener
    // This handles cases where header podlet loads asynchronously
    const setupListener = () => {
      const headerExists = document.querySelector('finn-topbar, nav');

      if (headerExists) {
        document.addEventListener('click', handleHeaderClick, true);
        return true;
      }

      return false;
    };

    // Try to set up listener immediately
    let listenerSetup = setupListener();

    // If header doesn't exist yet, retry with short delays (max 3 seconds)
    let retryCount = 0;
    const maxRetries = 5;
    const retryInterval = 300; // ms
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    if (!listenerSetup) {
      const retrySetup = () => {
        if (retryCount >= maxRetries) {
          console.warn('Header interceptor: Could not find header element after retries');
          return;
        }

        listenerSetup = setupListener();

        if (!listenerSetup) {
          retryCount++;
          retryTimer = setTimeout(retrySetup, retryInterval);
        }
      };

      retryTimer = setTimeout(retrySetup, retryInterval);
    }

    return () => {
      isListenerActive = false;
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
      document.removeEventListener('click', handleHeaderClick, true);
    };
  }, [location.pathname]);
};
